/**
 * ホームに出す数（layout-and-navigation.md §3）。進捗の状態とカードから毎回計算する。
 * キュー・ストリークの規則は src/lib/progress の関数をそのまま使い、ここでは数え上げだけをする。
 */
import type { Card } from '../../lib/cards';
import {
  activeDatesOf,
  addDays,
  buildDailyQueue,
  computeStreak,
  weekKey,
  type LocalDate,
  type ProgressState,
  type Settings,
} from '../../lib/progress';

export type WeekDayState = 'done' | 'rest' | 'none';
export type BoxCounts = [number, number, number, number, number];

export interface StageRow {
  stage: number;
  name: string;
  /** 出題するカードの数（draft を除く） */
  max: number;
  /** 1 回以上答えたカードの数 */
  done: number;
}

export interface HomeStats {
  /** 今日のキュー（カード id）。復習 → 新規 */
  queue: string[];
  reviewCount: number;
  newCount: number;
  boxes: BoxCounts;
  stages: StageRow[];
  streak: number;
  week: WeekDayState[];
  /** 今日の曜日（0 = 月曜） */
  todayIndex: number;
  freezeLeft: 0 | 1;
  todayDone: boolean;
}

export const STAGE_NAMES: Readonly<Record<number, string>> = {
  1: '入出力と型',
  2: 'ループと計算量',
  3: 'スタック・キュー・辞書',
};

/**
 * 今週（月曜始まり）の各日。休みは「その日を飛ばしたおかげでストリークが続いている日」。
 * computeStreak の結果を、その日より後の活動日だけで計算した値と比べて決める（規則は §5 のまま）。
 */
export function weekStates(
  activity: Readonly<Record<LocalDate, number>>,
  today: LocalDate,
): { week: WeekDayState[]; todayIndex: number; freezeLeft: 0 | 1 } {
  const active = activeDatesOf(activity);
  const monday = weekKey(today);
  const streak = computeStreak(active, today);
  const week: WeekDayState[] = [];
  let todayIndex = 0;
  let rested = false;
  for (let i = 0; i < 7; i++) {
    const day = addDays(monday, i);
    if (day === today) todayIndex = i;
    if (active.has(day)) {
      week.push('done');
    } else if (day < today && streak > 0) {
      const later = new Set([...active].filter((d) => d > day));
      const isRest = computeStreak(later, today) < streak;
      if (isRest) rested = true;
      week.push(isRest ? 'rest' : 'none');
    } else {
      week.push('none');
    }
  }
  return { week, todayIndex, freezeLeft: rested ? 0 : 1 };
}

export function homeStats(
  progress: ProgressState,
  settings: Settings,
  cards: readonly Card[],
  today: LocalDate,
): HomeStats {
  const queue = buildDailyQueue(progress, cards, today, settings);
  const reviewCount = queue.filter((id) => progress.cards[id] !== undefined).length;
  const boxes: BoxCounts = [0, 0, 0, 0, 0];
  const stageMap = new Map<number, StageRow>();
  for (const c of cards) {
    if (c.draft) continue;
    const rec = progress.cards[c.id];
    if (rec) boxes[rec.box - 1] = (boxes[rec.box - 1] ?? 0) + 1;
    let row = stageMap.get(c.stage);
    if (!row) {
      row = { stage: c.stage, name: STAGE_NAMES[c.stage] ?? '', max: 0, done: 0 };
      stageMap.set(c.stage, row);
    }
    row.max += 1;
    if (rec) row.done += 1;
  }
  const { week, todayIndex, freezeLeft } = weekStates(progress.activity, today);
  return {
    queue,
    reviewCount,
    newCount: queue.length - reviewCount,
    boxes,
    stages: [...stageMap.values()].sort((a, b) => a.stage - b.stage),
    streak: computeStreak(activeDatesOf(progress.activity), today),
    week,
    todayIndex,
    freezeLeft,
    todayDone: (progress.activity[today] ?? 0) > 0,
  };
}
